import { createContext, useEffect, useState } from "react";
import axios from "axios";
export const StoreContext = createContext(null);

// Configure axios defaults for CORS - withCredentials disabled to avoid CORS issues
// axios.defaults.withCredentials = true;
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Add axios response interceptor for better error handling
axios.interceptors.response.use(
    response => response,
    error => {
        // Log all axios errors for debugging
        if (error.response) {
            // The request was made and the server responded with a status code
            // outside the range of 2xx
            console.error('API Error Response:', {
                status: error.response.status,
                data: error.response.data,
                headers: error.response.headers
            });
        } else if (error.request) {
            // The request was made but no response was received
            console.error('API Error Request:', error.request);
        } else {
            // Something happened in setting up the request that triggered an Error
            console.error('API Error Message:', error.message);
        }
        
        return Promise.reject(error);
    }
);

const StoreContextProvider = (props) => {
    // Dynamic URL based on environment
    const getApiUrl = () => {
        const hostname = window.location.hostname;
        const isDevelopment = hostname === 'localhost' || hostname === '127.0.0.1';
        
        if (isDevelopment) {
            // Use the proxy in development to avoid CORS issues
            return '';
        } else {
            // Remove trailing slash if present to avoid double slashes
            const backendUrl = (import.meta.env.VITE_BACKEND_URL || 'https://dashbasket.onrender.com');
            return backendUrl.replace(/\/$/, '');
        }
    };
    
    const url = getApiUrl();
    const [food_list, setFoodList] = useState([]);
    const [menu_list, setMenuList] = useState([]);
    const [cartItems, setCartItems] = useState({});
    const [token, setToken] = useState("");
    const [isTokenLoading, setIsTokenLoading] = useState(true); // Track token loading state
    const [isUserDataLoading, setIsUserDataLoading] = useState(false); // Track user data loading state
    const [isFoodLoading, setIsFoodLoading] = useState(false); // Track food data loading state
    const [isMenuLoading, setIsMenuLoading] = useState(false); // Track menu data loading state
    const [username, setUsername] = useState(""); // Add username state
    const [profilePicture, setProfilePicture] = useState(""); // Add profile picture state
    const [userProfile, setUserProfile] = useState(null); // Add complete user profile state
    const [searchQuery, setSearchQuery] = useState(""); // Add search query state
    const [selectedRestaurant, setSelectedRestaurant] = useState(null);
    const [restaurant_list, setRestaurantList] = useState([]);
    const [userAddress, setUserAddress] = useState(null); // Add user address state
    const [activeOrderCount, setActiveOrderCount] = useState(0);
    const [notificationCount, setNotificationCount] = useState(0);
    const [otherNotificationCount, setOtherNotificationCount] = useState(0);
    const [socket, setSocket] = useState(null);
    const currency = "BIF";
    const [deliveryCharge, setDeliveryCharge] = useState(1000); // Default delivery fee in BIF
    // No tax - tax is included in item prices

    // Add request caching and deduplication
    const [requestCache, setRequestCache] = useState({});
    const [activeRequests, setActiveRequests] = useState(new Set());

    // Enhanced error handler for API calls
    const handleApiError = (error, operation) => {
        if (error.response) {
            // Handle rate limiting with exponential backoff
            if (error.response.status === 429) {
                console.warn(`Rate limited in ${operation}. Will retry with cached data if available.`);
                return;
            }
            console.error(`API Error in ${operation}:`, error.response.data);
        } else if (error.request) {
            console.error(`Network Error in ${operation}:`, error.message);
        } else {
            console.error(`Error in ${operation}:`, error.message);
        }
    };

    // Retry mechanism with exponential backoff
    const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000) => {
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                return await fn();
            } catch (error) {
                if (error.response?.status === 429 && attempt < maxRetries) {
                    const delay = baseDelay * Math.pow(2, attempt - 1);
                    console.log(`Rate limited, retrying in ${delay}ms (attempt ${attempt}/${maxRetries})`);
                    await new Promise(resolve => setTimeout(resolve, delay));
                    continue;
                } else {
                    throw error;
                }
            }
        }
    };

    // Save cart to localStorage whenever it changes
    useEffect(() => {
        if (Object.keys(cartItems).length > 0) {
            try {
                localStorage.setItem('cartItems', JSON.stringify(cartItems));
            } catch (error) {
                console.error('Error saving cart to localStorage:', error);
            }
        }
    }, [cartItems]);

    // Load cart from localStorage on component mount
    useEffect(() => {
        try {
            const savedCart = localStorage.getItem('cartItems');
            if (savedCart) {
                setCartItems(JSON.parse(savedCart));
            }
        } catch (error) {
            console.error('Error loading cart from localStorage:', error);
        }
    }, []);

    // Clear cart on server and localStorage
    const clearCart = async () => {
        try {
            if (token) {
                await axios.delete(url + "/api/cart/clear", { headers: { Authorization: `Bearer ${token}` } });
            }
            setCartItems({});
            localStorage.removeItem('cartItems');
        } catch (error) {
            handleApiError(error, 'clearing cart');
        }
    };

    const clearUserData = () => {
        setUsername("");
        setProfilePicture("");
        setUserProfile(null);
        setUserAddress(null);
        setActiveOrderCount(0);
        setNotificationCount(0);
        setOtherNotificationCount(0);
    };

    const addToCart = async (itemId) => {
        setCartItems((prev) => ({ ...prev, [itemId]: (prev[itemId] || 0) + 1 }));
        
        if (token) {
            try {
                await axios.post(url + "/api/cart/add", { itemId }, { headers: { Authorization: `Bearer ${token}` } });
            } catch (error) {
                handleApiError(error, 'adding to cart');
            }
        }
    };

    const removeFromCart = async (itemId) => {
        setCartItems((prev) => {
            const newItems = { ...prev };
            if (newItems[itemId] > 1) {
                newItems[itemId] -= 1;
            } else {
                delete newItems[itemId];
            }
            return newItems;
        });
        
        if (token) {
            try {
                await axios.delete(url + `/api/cart/remove/${itemId}`, { headers: { Authorization: `Bearer ${token}` } });
            } catch (error) {
                handleApiError(error, 'removing from cart');
            }
        }
    };

    const getTotalCartAmount = () => {
        try {
            let totalAmount = 0;
            for (const item in cartItems) {
                if (cartItems[item] > 0) {
                    let itemInfo = food_list.find((product) => product._id === item);
                    if (itemInfo) {
                        totalAmount += itemInfo.price * cartItems[item];
                    }
                }
            }
            return totalAmount;
        } catch (error) {
            console.error("Error calculating total:", error);
            return 0;
        }
    };

    const getTaxAmount = () => {
        // No tax - tax is included in item prices
        return 0;
    };

    const getGrandTotal = () => {
        const subtotal = getTotalCartAmount();
        const tax = getTaxAmount(); // Always 0
        const delivery = subtotal === 0 ? 0 : deliveryCharge;
        return subtotal + tax + delivery;
    };

    // Calculate delivery fee based on restaurant and customer location
    const calculateDeliveryFee = async (restaurantId, customerAddress) => {
        if (!restaurantId || !customerAddress) {
            console.warn('Missing restaurant ID or customer address for delivery fee calculation');
            return 1000; // Default fee
        }

        try {
            const response = await axios.post(`${url}/api/delivery/calculate-fee`, {
                restaurantId,
                customerAddress
            });

            if (response.data.success) {
                const fee = response.data.data.deliveryFee;
                setDeliveryCharge(fee);
                return fee;
            } else {
                console.warn('Failed to calculate delivery fee:', response.data.message);
                return 1000; // Default fee
            }
        } catch (error) {
            console.warn('Error calculating delivery fee:', error);
            return 1000; // Default fee
        }
    };

    // Format image URL to handle both local and remote URLs
    const formatImageUrl = (imgUrl) => {
        if (!imgUrl) return null;
        
        // If it's already a data URL or blob URL, return as is
        if (imgUrl.startsWith('data:') || imgUrl.startsWith('blob:')) {
            return imgUrl;
        }
        
        // Backend URL
        const BACKEND_URL = url;
        
        // Handle different URL formats
        let fullUrl;
        
        // Case 1: URL starts with /uploads/ - append to backend URL
        if (imgUrl.startsWith('/uploads/')) {
            fullUrl = `${BACKEND_URL}${imgUrl}`;
        } 
        // Case 2: URL is just a filename (likely from database) - append to uploads path
        else if (!imgUrl.startsWith('http') && !imgUrl.includes('/')) {
            fullUrl = `${BACKEND_URL}/uploads/${imgUrl}`;
        }
        // Case 3: URL is a relative path - append to backend
        else if (!imgUrl.startsWith('http')) {
            fullUrl = `${BACKEND_URL}/${imgUrl}`;
        }
        // Case 4: Full URL - use as is
        else {
            fullUrl = imgUrl;
        }
        
        return fullUrl;
    };

    const fetchRestaurantList = async (page = 1, limit = 50) => {
        const cacheKey = `restaurants_${page}_${limit}`;
        const requestKey = `fetch_restaurants_${page}_${limit}`;

        // Return cached data if available and not stale
        if (requestCache[cacheKey] && Date.now() - requestCache[cacheKey].timestamp < 300000) { // 5 minutes cache
            console.log('StoreContext: Using cached restaurant data');
            setRestaurantList(requestCache[cacheKey].data);
            return requestCache[cacheKey].data;
        }

        // Prevent duplicate requests
        if (activeRequests.has(requestKey)) {
            console.log('StoreContext: Restaurant request already in progress, skipping duplicate');
            return [];
        }

        try {
            setActiveRequests(prev => new Set([...prev, requestKey]));
            const response = await axios.get(`${url}/api/restaurant?page=${page}&limit=${limit}`);
            
            if (response.data.success && response.data.data && response.data.data.restaurants) {
                const restaurants = response.data.data.restaurants;
                setRestaurantList(restaurants);
                
                // Cache the result
                setRequestCache(prev => ({
                    ...prev,
                    [cacheKey]: {
                        data: restaurants,
                        timestamp: Date.now()
                    }
                }));
                
                // Don't auto-select a restaurant on initial load
                // Let the current page (Home or Restaurant) decide what to do
                console.log(`StoreContext: Loaded ${restaurants.length} restaurants`)
                
                return restaurants;
            } else {
                console.error("Unexpected restaurant data format:", response.data);
                return [];
            }
        } catch (error) {
            handleApiError(error, 'fetching restaurant list');
            return [];
        } finally {
            setActiveRequests(prev => {
                const newSet = new Set(prev);
                newSet.delete(requestKey);
                return newSet;
            });
        }
    };

    const fetchIndividualRestaurant = async (restaurantId) => {
        try {
            const response = await axios.get(`${url}/api/restaurant/${restaurantId}`);
            
            if (response.data.success && response.data.data) {
                const restaurant = response.data.data;
                setSelectedRestaurant(restaurant);
                
                // Add to restaurant list if not already there
                setRestaurantList(prevList => {
                    const exists = prevList.some(r => r._id === restaurant._id);
                    return exists ? prevList : [...prevList, restaurant];
                });
                
                return restaurant;
            }
            return null;
        } catch (error) {
            handleApiError(error, 'fetching individual restaurant');
            return null;
        }
    };

    const fetchFoodList = async (restaurantId = null, manageLoadingState = true) => {
        const cacheKey = `food_${restaurantId || 'general'}`;
        const requestKey = `fetch_food_${restaurantId || 'general'}`;

        // Return cached data if available and not stale
        if (requestCache[cacheKey] && Date.now() - requestCache[cacheKey].timestamp < 180000) { // 3 minutes cache
            console.log('StoreContext: Using cached food data');
            setFoodList(requestCache[cacheKey].data);
            return;
        }

        // Prevent duplicate requests
        if (activeRequests.has(requestKey)) {
            console.log('StoreContext: Food request already in progress, skipping duplicate');
            return;
        }

        if (manageLoadingState) {
            setIsFoodLoading(true);
        }
        try {
            setActiveRequests(prev => new Set([...prev, requestKey]));
            let response;
            
            if (restaurantId) {
                console.log('StoreContext: Fetching food items for restaurant:', restaurantId)
                response = await axios.get(`${url}/api/food-management/restaurants/${restaurantId}/food-items`);
            } else {
                console.log('StoreContext: Fetching general food items for home page')
                response = await axios.get(`${url}/api/food/list`);
            }
            
            if (response.data.success) {
                // Handle different response formats from different endpoints
                const foodItems = response.data.data || response.data.foods || [];
                
                if (Array.isArray(foodItems)) {
                    const normalizedItems = foodItems.map(item => ({
                        ...item,
                        category: item.menu?.name || item.menu?.category || item.category || item.categoryName || 'Uncategorized'
                    }));
                    
                    console.log(`StoreContext: Successfully loaded ${normalizedItems.length} food items`)
                    setFoodList(normalizedItems);

                    // Cache the result
                    setRequestCache(prev => ({
                        ...prev,
                        [cacheKey]: {
                            data: normalizedItems,
                            timestamp: Date.now()
                        }
                    }));
                } else {
                    console.warn("Unexpected food data structure:", response.data);
                    setFoodList([]);
                }
            } else {
                console.error("API returned error:", response.data.message);
                setFoodList([]);
            }
        } catch (error) {
            handleApiError(error, 'fetching food list');
            setFoodList([]);
        } finally {
            if (manageLoadingState) {
                setIsFoodLoading(false);
            }
            setActiveRequests(prev => {
                const newSet = new Set(prev);
                newSet.delete(requestKey);
                return newSet;
            });
        }
    };

    const fetchMenuList = async (restaurantId = null, manageLoadingState = true) => {
        const cacheKey = `menu_${restaurantId || 'general'}`;
        const requestKey = `fetch_menu_${restaurantId || 'general'}`;

        // Return cached data if available and not stale
        if (requestCache[cacheKey] && Date.now() - requestCache[cacheKey].timestamp < 300000) { // 5 minutes cache
            console.log('StoreContext: Using cached menu data');
            setMenuList(requestCache[cacheKey].data);
            return;
        }

        // Prevent duplicate requests
        if (activeRequests.has(requestKey)) {
            console.log('StoreContext: Menu request already in progress, skipping duplicate');
            return;
        }

        if (manageLoadingState) {
            setIsMenuLoading(true);
        }
        try {
            setActiveRequests(prev => new Set([...prev, requestKey]));
            let response;
            
            if (restaurantId) {
                console.log('StoreContext: Fetching menu categories for restaurant:', restaurantId)
                response = await axios.get(`${url}/api/food-management/restaurants/${restaurantId}/menu-categories`);
            } else {
                console.log('StoreContext: Fetching general menu categories')
                response = await axios.get(`${url}/api/menu/list`);
            }
            
            if (response.data.success) {
                // Handle different response formats from different endpoints
                const rawMenus = response.data.data || response.data.menus || [];
                const normalizedMenus = rawMenus.map(menu => ({
                    ...menu,
                    menu_name: menu.name || menu.menu_name || menu.categoryName || 'Unnamed Category',
                    menu_image: menu.image || menu.menu_image || null
                }));
                
                console.log(`StoreContext: Successfully loaded ${normalizedMenus.length} menu categories`)
                setMenuList(normalizedMenus);

                // Cache the result
                setRequestCache(prev => ({
                    ...prev,
                    [cacheKey]: {
                        data: normalizedMenus,
                        timestamp: Date.now()
                    }
                }));
            } else {
                console.error("Menu API error:", response.data.message);
                setMenuList([]);
            }
        } catch (error) {
            handleApiError(error, 'fetching menu list');
            setMenuList([]);
        } finally {
            if (manageLoadingState) {
                setIsMenuLoading(false);
            }
            setActiveRequests(prev => {
                const newSet = new Set(prev);
                newSet.delete(requestKey);
                return newSet;
            });
        }
    };

    const loadCartData = async (token) => {
        const requestKey = 'load_cart_data';

        // Prevent duplicate requests
        if (activeRequests.has(requestKey)) {
            console.log('StoreContext: Cart data request already in progress, skipping duplicate');
            return;
        }

        try {
            setActiveRequests(prev => new Set([...prev, requestKey]));
            const response = await axios.get(url + "/api/cart", { headers: { Authorization: `Bearer ${token}` } });
            if (response.data.success) {
                setCartItems(response.data.cartData || {});
            }
        } catch (error) {
            handleApiError(error, 'loading cart data');
        } finally {
            setActiveRequests(prev => {
                const newSet = new Set(prev);
                newSet.delete(requestKey);
                return newSet;
            });
        }
    };

    const fetchUserData = async (token) => {
        setIsUserDataLoading(true);
        try {
            const response = await axios.get(url + "/api/user/profile", { headers: { Authorization: `Bearer ${token}` } });
            const userData = response.data.user;
            
            // Validate that we have a valid user with a name
            if (!userData || !userData.name || userData.name.trim() === '') {
                console.warn("Invalid user data received:", userData);
                // Clear user data if no valid name
                setUsername("");
                setProfilePicture("");
                setUserProfile(null);
                setUserAddress(null);
                // Also clear the token since it's invalid
                localStorage.removeItem("token");
                setToken("");
                return;
            }
            
            setUsername(userData.name); // Set the username
            
            // Set the complete user profile
            setUserProfile({
                name: userData.name || '',
                email: userData.email || '',
                phone: userData.phone || '',
                // Parse name into firstName and lastName if available
                firstName: userData.name ? userData.name.split(' ')[0] : '',
                lastName: userData.name ? userData.name.split(' ').slice(1).join(' ') : ''
            });
            
            // Set the profile picture if available
            if (userData.profilePicture) {
                setProfilePicture(formatImageUrl(userData.profilePicture));
            }
            
            // Set the user address if available
            if (userData.address) {
                setUserAddress(userData.address);
            }
        } catch (error) {
            console.error("Error fetching user data:", error);
            // Clear user data on error
            setUsername("");
            setProfilePicture("");
            setUserProfile(null);
            setUserAddress(null);
        } finally {
            setIsUserDataLoading(false);
        }
    };

    const updateUserAddress = async (addressData) => {
        try {
            const response = await axios.put(url + "/api/user/address", addressData, { 
                headers: { Authorization: `Bearer ${token}` } 
            });
            
            if (response.data.success) {
                setUserAddress(response.data.address);
                return { success: true, address: response.data.address };
            } else {
                return { success: false, error: response.data.message };
            }
        } catch (error) {
            console.error("Error updating user address:", error);
            return { success: false, error: error.response?.data?.message || error.message };
        }
    };

    const updateUserProfile = async (profileData) => {
        try {
            const response = await axios.put(url + "/api/user/profile", profileData, { 
                headers: { Authorization: `Bearer ${token}` } 
            });
            
            if (response.data.success) {
                const updatedProfile = response.data.user;
                setUserProfile({
                    name: updatedProfile.name || '',
                    email: updatedProfile.email || '',
                    phone: updatedProfile.phone || '',
                    firstName: updatedProfile.name ? updatedProfile.name.split(' ')[0] : '',
                    lastName: updatedProfile.name ? updatedProfile.name.split(' ').slice(1).join(' ') : ''
                });
                setUsername(updatedProfile.name);
                return { success: true, profile: updatedProfile };
            } else {
                return { success: false, error: response.data.message };
            }
        } catch (error) {
            console.error("Error updating user profile:", error);
            return { success: false, error: error.response?.data?.message || error.message };
        }
    };

    const getFormattedAddress = () => {
        if (!userAddress) return null;
        
        const parts = [
            userAddress.street,
            userAddress.quartier,
            userAddress.city,
            userAddress.state,
            userAddress.country
        ].filter(part => part && part.trim());
        
        return parts.length > 0 ? parts.join(', ') : null;
    };

    const updateSearchQuery = (query) => {
        setSearchQuery(query);
    };

    const refreshFoodData = async (restaurantId = null) => {
        // If restaurantId is explicitly passed (even as null), use that value
        // Otherwise, fall back to selectedRestaurant._id
        const targetRestaurantId = restaurantId !== null ? restaurantId : selectedRestaurant?._id;
        
        
        
        // Set loading state immediately to prevent "no results" flash
        setIsFoodLoading(true);
        setIsMenuLoading(true);
        
        try {
            if (targetRestaurantId) {
                await Promise.all([
                    fetchFoodList(targetRestaurantId, false),
                    fetchMenuList(targetRestaurantId, false)
                ]);
            } else {
                await Promise.all([
                    fetchFoodList(null, false),
                    fetchMenuList(null, false)
                ]);
            }
        } catch (error) {
            console.error('Error refreshing food data:', error);
        } finally {
            // Ensure loading states are set to false after completion
            setIsFoodLoading(false);
            setIsMenuLoading(false);
        }
    };

    useEffect(() => {
        async function loadData() {
            // Set loading states immediately to prevent "no results" flash on page refresh
            setIsFoodLoading(true);
            setIsMenuLoading(true);
            
            await fetchRestaurantList();
            
            // Load general food items and menu categories for the home page

            await Promise.all([
                fetchFoodList(null, false), // Don't manage loading state here
                fetchMenuList(null, false)  // Don't manage loading state here
            ]);
            
            if (localStorage.getItem("token")) {
                const savedToken = localStorage.getItem("token");
                setToken(savedToken);
                try {
                    // Load cart and user data in parallel for better performance
                    await Promise.all([
                        loadCartData(savedToken),
                        fetchUserData(savedToken)
                    ]);
                } catch (error) {
                    console.error("Error loading user data:", error);
                    // If user data fails to load, clear the token to force re-authentication
                    localStorage.removeItem("token");
                    setToken("");
                }
            } else {
                // No token found, ensure user data is cleared
                setUsername("");
                setProfilePicture("");
                setUserProfile(null);
                setUserAddress(null);
            }
            
            // Mark token loading as complete
            setIsTokenLoading(false);
        }
        
        // Call loadData and handle errors
        loadData().catch(error => {
            console.error("Error in initial data loading:", error);
        }).finally(() => {
            // Ensure loading states are set to false after completion
            setIsFoodLoading(false);
            setIsMenuLoading(false);
        });
    }, []);

    useEffect(() => {
        if (selectedRestaurant?._id) {

            // Small delay to prevent race conditions during navigation
            const timeoutId = setTimeout(() => {
                refreshFoodData(selectedRestaurant._id);
            }, 50);
            return () => clearTimeout(timeoutId);
        } else if (selectedRestaurant === null) {

            // Small delay to prevent race conditions during navigation  
            const timeoutId = setTimeout(() => {
                refreshFoodData(null);
            }, 50);
            return () => clearTimeout(timeoutId);
        }
    }, [selectedRestaurant]);

    // Fetch active order count for notifications
    const fetchActiveOrderCount = async () => {
        if (!token) {
            setActiveOrderCount(0);
            return;
        }

        try {
            const response = await axios.post(
                `${url}/api/order/userorders`,
                {
                    status: undefined, // Get all orders
                    limit: 100,
                    page: 1
                },
                { 
                    headers: { 
                        Authorization: `Bearer ${token}` 
                    } 
                }
            );
            
            if (response.data.success) {
                const orders = response.data.data?.orders || [];
                // Count active orders (not delivered, cancelled, or final status)
                const activeOrders = orders.filter(order => {
                    // For pickup orders, "Picked Up" is the final status
                    if (order.deliveryType === 'pickup') {
                        return order.status !== 'Delivered' && 
                               order.status !== 'Cancelled' && 
                               order.status !== 'Failed' && 
                               order.status !== 'Picked Up';
                    }
                    // For delivery orders, use standard logic
                    return order.status !== 'Delivered' && 
                           order.status !== 'Cancelled' && 
                           order.status !== 'Failed';
                });
                
                setActiveOrderCount(activeOrders.length);
            }
        } catch (error) {
            console.error('Error fetching active order count:', error);
            setActiveOrderCount(0);
        }
    };

    // Fetch other notifications (promotions, updates, etc.)
    const fetchOtherNotifications = async () => {
        if (!token) {
            setOtherNotificationCount(0);
            return;
        }

        try {
            // This would be replaced with actual API call for other notifications
            // For now, we'll simulate some notifications for testing
            const mockNotifications = [
                // Example: { type: 'promotion', title: 'New Restaurant Added', read: false },
                // Example: { type: 'update', title: 'App Update Available', read: false }
            ];
            
            // For testing: simulate 2 unread notifications
            // Remove this line in production and use actual API data
            const unreadCount = 2; // mockNotifications.filter(n => !n.read).length;
            setOtherNotificationCount(unreadCount);
        } catch (error) {
            console.error('Error fetching other notifications:', error);
            setOtherNotificationCount(0);
        }
    };

    // Update notification count when orders change
    const updateNotificationCount = (newCount) => {
        setActiveOrderCount(newCount);
    };

    // Update other notification count
    const updateOtherNotificationCount = (newCount) => {
        setOtherNotificationCount(newCount);
    };

    // Fetch active order count on token change
    useEffect(() => {
        fetchActiveOrderCount();
        fetchOtherNotifications();
        
        // Set up interval to refresh order count every 30 seconds
        const interval = setInterval(() => {
            if (token) {
                fetchActiveOrderCount();
                fetchOtherNotifications();
            }
        }, 30000);

        return () => clearInterval(interval);
    }, [token]);

    // Calculate total notification count
    useEffect(() => {
        const totalCount = activeOrderCount + otherNotificationCount;
        setNotificationCount(totalCount);
    }, [activeOrderCount, otherNotificationCount]);

    // WebSocket connection for real-time updates - temporarily disabled
    // useEffect(() => {
    //     if (token && userProfile?._id) {
    //         try {
    //             // Connect to WebSocket server
    //             const socketUrl = url.replace('http', 'ws');
    //             const newSocket = new WebSocket(`${socketUrl.replace('https', 'wss')}`);
    //             
    //             newSocket.onopen = () => {
    //                 console.log('WebSocket connected for order notifications');
    //                 // Join user room for order updates
    //                 newSocket.send(JSON.stringify({
    //                     type: 'join_user_room',
    //                     userId: userProfile._id
    //                 }));
    //             };
    //             
    //             newSocket.onmessage = (event) => {
    //                 try {
    //                     const data = JSON.parse(event.data);
    //                     
    //                     if (data.type === 'order_status_changed') {
    //                         // Update notification count based on order status change
    //                         if (data.isActiveOrder) {
    //                             // Order became active (e.g., from cancelled to created)
    //                             setNotificationCount(prev => prev + 1);
    //                             setActiveOrderCount(prev => prev + 1);
    //                         } else {
    //                             // Order became inactive (e.g., delivered, cancelled)
    //                             setNotificationCount(prev => Math.max(0, prev - 1));
    //                             setActiveOrderCount(prev => Math.max(0, prev - 1));
    //                         }
    //                     }
    //                 } catch (error) {
    //                     console.error('Error parsing WebSocket message:', error);
    //                 }
    //             };
    //             
    //             newSocket.onerror = (error) => {
    //                 console.error('WebSocket error:', error);
    //             };
    //             
    //             newSocket.onclose = () => {
    //                 console.log('WebSocket disconnected');
    //             };
    //             
    //             setSocket(newSocket);
    //             
    //             return () => {
    //                 if (newSocket.readyState === WebSocket.OPEN) {
    //                     newSocket.close();
    //                 }
    //             };
    //         } catch (error) {
    //             console.error('Error setting up WebSocket:', error);
    //         }
    //     }
    // }, [token, userProfile?._id, url]);

    const contextValue = {
        food_list,
        menu_list,
        restaurant_list,
        selectedRestaurant,
        setSelectedRestaurant,
        cartItems,
        setCartItems,
        addToCart,
        removeFromCart,
        getTotalCartAmount,
        getTaxAmount,
        getGrandTotal,
        calculateDeliveryFee,
        deliveryCharge,
        setDeliveryCharge,
        url,
        token,
        setToken,
        isTokenLoading,
        isUserDataLoading,
        isFoodLoading,
        isMenuLoading,
        clearCart,
        clearUserData,
        loadCartData,
        refreshFoodData,
        fetchRestaurantList,
        fetchIndividualRestaurant,
        fetchUserData,
        updateUserAddress,
        updateUserProfile,
        getFormattedAddress,
        userAddress,
        username,
        setUsername,
        profilePicture,
        setProfilePicture,
        userProfile,
        setUserProfile,
        formatImageUrl,
        searchQuery,
        updateSearchQuery,
        currency,
        activeOrderCount,
        notificationCount,
        otherNotificationCount,
        updateNotificationCount,
        updateOtherNotificationCount,
        fetchActiveOrderCount,
        fetchOtherNotifications
    };

    return (
        <StoreContext.Provider value={contextValue}>
            {props.children}
        </StoreContext.Provider>
    );
};

export default StoreContextProvider;